import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, HeartIcon, ShieldCheckIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

const PromiseToParents = () => {
    const promises = [
        {
            title: "A Balanced Education",
            description: "We combine academic rigour with spiritual development, ensuring that your child thrives both in the Dunyah and Akhirah. From Cambridge curriculum to Islamic Tarbiyah and Qur'anic learning, we offer an education rooted in both excellence and values.",
            icon: <AcademicCapIcon className="w-8 h-8" />
        },
        {
            title: "A Nurturing Environment",
            description: "We create a safe, loving and stimulating space where your child feels heard, supported and confident. Our teachers and staff treat every child with care, dignity and individual attention.",
            icon: <HeartIcon className="w-8 h-8" />
        },
        {
            title: "Transparent Communication",
            description: "We believe in working as partners with parents. You'll always be informed, involved and welcome — whether through updates, meetings, or open door conversations.",
            icon: <ShieldCheckIcon className="w-8 h-8" />
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={require('../../assets/Promise to Parents - Hero image.jpg')}
                        alt="Aureus Cambridge School students"
                        className="object-cover w-full h-full"
                    />
                </div>
                <div className="absolute inset-0 bg-black/60" />

                <div className="relative px-4 mx-auto text-center max-w-7xl">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mb-8 text-4xl font-bold text-white md:text-6xl"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                        OUR <span className="text-yellow-400">PROMISE</span>
                        <br />TO PARENTS
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="max-w-4xl mx-auto text-xl md:text-2xl text-white/90"
                    >
                        A sacred commitment to your child's growth, development, and future success
                    </motion.p>
                </div>
            </section>

            {/* Main Promise Section */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-10 text-center"
                    >
                        <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                            Our Sacred <span className="text-yellow-500">Commitment</span>
                        </h2>
                        <div className="max-w-4xl p-8 mx-auto bg-gray-50 rounded-2xl md:p-12">
                            <p className="text-lg leading-relaxed text-gray-700 md:text-xl">
                                At Aureus Cambridge School, we understand that choosing the right school is one of the most important decisions you will make for your child.                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Our Promises Grid */}
            <section className="py-10 bg-gray-50">
                <div className="max-w-6xl px-4 mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="mb-16 text-center"
                    >
                        <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
                            What We <span className="text-yellow-500">Promise</span>
                        </h2>
                        <p className="max-w-3xl mx-auto text-xl text-gray-600">
                            These are the commitments we make to every family that joins our school community
                        </p>
                    </motion.div>

                    <div className="grid gap-8 md:grid-cols-3">
                        {promises.map((promise, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="p-8 text-center transition-shadow duration-300 bg-white shadow-lg rounded-2xl hover:shadow-xl"
                            >
                                <div className="flex justify-center mb-6 text-yellow-500">
                                    {promise.icon}
                                </div>
                                <h3 className="mb-4 text-xl font-bold text-gray-900">{promise.title}</h3>
                                <p className="leading-relaxed text-gray-600">{promise.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Additional Promises Section */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl px-4 mx-auto">
                    <div className="grid items-center gap-12 md:grid-cols-2">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                                Our Additional <span className="text-yellow-500">Promises</span>
                            </h2>
                            <div className="space-y-6">
                                <div className="flex items-start space-x-4">
                                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 bg-yellow-400 rounded-full">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-gray-900">Growth with Purpose</h3>
                                        <p className="text-gray-600">We aim to raise students who are morally strong, socially responsible and emotionally intelligent, prepared not just for exams, but for life.</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-4">
                                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 bg-yellow-400 rounded-full">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-lg font-semibold text-gray-900">A School You Can Trust</h3>
                                        <p className="text-gray-600">Every system, decision and activity is guided by integrity, professionalism and the well being of your child.</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <img
                                src={require('../../assets/update/Additional Promises.jpeg')}
                                alt="Teacher and student"
                                className="w-full h-auto shadow-lg rounded-2xl"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-yellow-400">
                <div className="max-w-4xl px-4 mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="mb-8 text-3xl font-bold text-black md:text-4xl">
                            Ready to Join Our School Family?
                        </h2>
                        <p className="max-w-2xl mx-auto mb-8 text-xl text-black">
                            Experience our promise first-hand. Schedule a visit to see how we nurture young minds and build bright futures.
                        </p>
                        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-white transition-colors duration-300 bg-gray-900 rounded-full sm:w-auto hover:bg-gray-800 group"
                            >
                                Schedule Visit
                                <ChevronRightIcon className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/apply-online"
                                className="inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold text-gray-900 transition-colors duration-300 border-2 border-gray-900 rounded-full sm:w-auto hover:bg-gray-900 hover:text-white"
                            >
                                Apply Now
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default PromiseToParents;
